import mixpanel from "mixpanel-browser";
import { User } from "shared/datamodel/schemas";
import { isCIM } from "./url-utils";
import { readCookieValue } from "./cookie-utils";
import consts from "shared/consts";
import { prepareAccountObjectForAnalytics } from "./users-utils";

const IS_PROD_ENV = process.env.NEXT_ENV === "production";

export interface AnalyticsTrackOptions {
  transport?: "sendBeacon" | "xhr";
}

declare global {
  interface Window {
    _DATADOG_SYNTHETICS_BROWSER: unknown;
  }
}

function getPublicOptOutTracking() {
  return !IS_PROD_ENV || window?._DATADOG_SYNTHETICS_BROWSER !== undefined;
}

function initMixPanelClient() {
  const source = readCookieValue(consts.COOKIE_NAMES.SOURCE);
  mixpanel.init(process.env.MIXPANEL_TOKEN ?? "", { debug: false, api_host: "https://mixpanel.workcanvas.com" });
  mixpanel.register_once({
    page_url: window.location.href,
    referrer: source,
  });
  const optOutTracking = getPublicOptOutTracking();

  if (optOutTracking) {
    mixpanel.opt_out_tracking();
  }
}

/**
 * Tracks a mixpanel event
 * @param {string} event - The name of the event to track
 * @param {Record<string, unknown>} props - Properties to include with the event
 * @param {number} retriesNumber - Number of retries attempted (internal use)
 * @param {AnalyticsTrackOptions} options - Options for tracking
 */
function trackMixPanel(
  event: string,
  props: Record<string, unknown>,
  retriesNumber = 0,
  options?: AnalyticsTrackOptions
) {
  const MAX_RETRIES_NUMBER = 2;
  if (retriesNumber < MAX_RETRIES_NUMBER) {
    try {
      if (options) {
        mixpanel.track(event, props, {
          transport: options.transport,
        });
      } else {
        mixpanel.track(event, props);
      }
    } catch {
      //when a user enters directly to a canvas, we need to initialized the client first since it is being initialized in boards page
      console.log("error sending mix panel event, retrying");
      initMixPanelClient();
      trackMixPanel(event, props, ++retriesNumber, options);
    }
  } else {
    console.log("max retry limit reached, skipping");
  }
}

function registerMixpanelPropertiesOnce(properties: Record<string, unknown>) {
  mixpanel.register_once(properties);
  mixpanel.people.set_once(properties);
}

function identifyUser(userId: string) {
  if (!userId) return;
  // This links the session. All events after this line are safe.
  mixpanel.identify(userId);
}

function initMixPanelUser(user: User) {
  if (typeof window === "undefined") return;

  mixpanel.identify(user.id.toString());
  mixpanel.register(user);
  mixpanel.people.set(user);

  if (user?.account) {
    const accountId = user?.accountId || user.account?.id || "";
    mixpanel.set_group("account", `id::${accountId}`);
    mixpanel.get_group("account", `id::${accountId}`).set(prepareAccountObjectForAnalytics(user));
  }

  setAuthorizedOptedTracking(user);
}

function setAuthorizedOptedTracking(user: User) {
  const shouldOptOutUser = getPublicOptOutTracking() || (user.account?.id === "5" && !isCIM());

  const isOptedOutUser = mixpanel.has_opted_out_tracking();

  if (isOptedOutUser && !shouldOptOutUser) {
    mixpanel.opt_in_tracking();
  }

  if (shouldOptOutUser) {
    mixpanel.opt_out_tracking(); //don't send data for monday account users or in dev or for datadog bots
  }
}

export { trackMixPanel, registerMixpanelPropertiesOnce, initMixPanelClient, initMixPanelUser, identifyUser };
